// Shared data source for Chart of Accounts and OrgUnits
// This ensures all components use the same data and stay in sync

export interface OrgUnit {
  id: string
  code: string
  name: string
  glCodeSegment?: string
  participatesInGLCoding: boolean
  path: string
  parentOrgUnitId?: string
  childOrgUnits?: OrgUnit[]
  orgUnitType?: {
    id: string
    name: string
    code: string
  }
}

export interface COASegment {
  id: string
  segmentCode: string
  segmentName: string
  accountTypeId: string
  parentId?: string
  isActive: boolean
  activeFrom?: string
  activeTo?: string
  orgUnitId?: string
  fullGLPath: string
  level: number
  children?: COASegment[]
  scopeType?: 'global' | 'specific'
  applicableOrgUnitIds?: string[]
}

export interface AccountType {
  id: string
  name: string
  description?: string
  color: string
}

// Sample OrgUnits data
export const sampleOrgUnits: OrgUnit[] = [
  {
    id: 'org-001',
    code: 'GLOBAL',
    name: 'Global Technologies Inc.',
    glCodeSegment: '100',
    participatesInGLCoding: true,
    path: '/GLOBAL',
    orgUnitType: {
      id: 'type-001',
      name: 'Legal Entity',
      code: 'ENTITY',
    },
    childOrgUnits: [
      {
        id: 'org-002',
        code: 'NY-HQ',
        name: 'New York Headquarters',
        glCodeSegment: '110',
        participatesInGLCoding: true,
        path: '/GLOBAL/NY-HQ',
        parentOrgUnitId: 'org-001',
        orgUnitType: {
          id: 'type-002',
          name: 'Physical Site',
          code: 'SITE',
        },
        childOrgUnits: [
          {
            id: 'org-005',
            code: 'NY-FIN',
            name: 'Finance Department',
            glCodeSegment: '115',
            participatesInGLCoding: true,
            path: '/GLOBAL/NY-HQ/NY-FIN',
            parentOrgUnitId: 'org-002',
            orgUnitType: {
              id: 'type-003',
              name: 'Department',
              code: 'DEPT',
            },
          },
        ],
      },
      {
        id: 'org-003',
        code: 'SF-OFFICE',
        name: 'San Francisco Office',
        glCodeSegment: '120',
        participatesInGLCoding: true,
        path: '/GLOBAL/SF-OFFICE',
        parentOrgUnitId: 'org-001',
        orgUnitType: {
          id: 'type-002',
          name: 'Physical Site',
          code: 'SITE',
        },
      },
    ],
  },
  {
    id: 'org-004',
    code: 'EURO-OPS',
    name: 'European Operations Ltd',
    glCodeSegment: '200',
    participatesInGLCoding: true,
    path: '/EURO-OPS',
    orgUnitType: {
      id: 'type-001',
      name: 'Legal Entity',
      code: 'ENTITY',
    },
  },
  {
    id: 'org-006',
    code: 'APAC-PTY',
    name: 'Asia Pacific Technologies Pty Ltd',
    glCodeSegment: '300',
    participatesInGLCoding: true,
    path: '/APAC-PTY',
    orgUnitType: {
      id: 'type-001',
      name: 'Legal Entity',
      code: 'ENTITY',
    },
  },
]

// Sample Account Types
export const sampleAccountTypes: AccountType[] = [
  {
    id: 'at-001',
    name: 'Asset',
    description: 'Resources owned by the company',
    color: 'blue',
  },
  {
    id: 'at-002',
    name: 'Liability',
    description: 'Obligations owed by the company',
    color: 'red',
  },
  {
    id: 'at-003',
    name: 'Equity',
    description: 'Ownership interest in the company',
    color: 'green',
  },
  {
    id: 'at-004',
    name: 'Revenue',
    description: 'Income earned from business activities',
    color: 'purple',
  },
  {
    id: 'at-005',
    name: 'Expense',
    description: 'Costs incurred in business operations',
    color: 'orange',
  },
]

// Sample COA data with deep hierarchies (up to 5 levels after OrgUnit)
export const sampleCOAData: COASegment[] = [
  {
    id: 'coa-001',
    segmentCode: '1000',
    segmentName: 'Assets',
    accountTypeId: 'at-001',
    isActive: true,
    activeFrom: '2023-01-01',
    fullGLPath: '1000',
    level: 1,
    scopeType: 'global',
    children: [
      {
        id: 'coa-002',
        segmentCode: '1100',
        segmentName: 'Current Assets',
        accountTypeId: 'at-001',
        parentId: 'coa-001',
        isActive: true,
        activeFrom: '2023-01-01',
        fullGLPath: '1000.1100',
        level: 2,
        scopeType: 'global',
        children: [
          {
            id: 'coa-003',
            segmentCode: '1110',
            segmentName: 'Cash and Cash Equivalents',
            accountTypeId: 'at-001',
            parentId: 'coa-002',
            isActive: true,
            activeFrom: '2023-01-01',
            fullGLPath: '1000.1100.1110',
            level: 3,
            scopeType: 'global',
            children: [
              {
                id: 'coa-019',
                segmentCode: '1111',
                segmentName: 'Petty Cash',
                accountTypeId: 'at-001',
                parentId: 'coa-003',
                isActive: true,
                activeFrom: '2023-01-01',
                fullGLPath: '1000.1100.1110.1111',
                level: 4,
                scopeType: 'global',
                children: [
                  {
                    id: 'coa-020',
                    segmentCode: '1111A',
                    segmentName: 'Office Petty Cash',
                    accountTypeId: 'at-001',
                    parentId: 'coa-019',
                    isActive: true,
                    activeFrom: '2023-01-01',
                    fullGLPath: '1000.1100.1110.1111.1111A',
                    level: 5,
                    scopeType: 'global',
                    children: [],
                  },
                ],
              },
            ],
          },
          {
            id: 'coa-004',
            segmentCode: '1120',
            segmentName: 'Accounts Receivable',
            accountTypeId: 'at-001',
            parentId: 'coa-002',
            isActive: true,
            activeFrom: '2023-01-01',
            fullGLPath: '1000.1100.1120',
            level: 3,
            scopeType: 'global',
            children: [],
          },
        ],
      },
      {
        id: 'coa-005',
        segmentCode: '1200',
        segmentName: 'Non-Current Assets',
        accountTypeId: 'at-001',
        parentId: 'coa-001',
        isActive: true,
        activeFrom: '2023-01-01',
        fullGLPath: '1000.1200',
        level: 2,
        scopeType: 'global',
        children: [],
      },
    ],
  },
  {
    id: 'coa-006',
    segmentCode: '2000',
    segmentName: 'Liabilities',
    accountTypeId: 'at-002',
    isActive: true,
    activeFrom: '2023-01-01',
    fullGLPath: '2000',
    level: 1,
    scopeType: 'global',
    children: [
      {
        id: 'coa-007',
        segmentCode: '2100',
        segmentName: 'Current Liabilities',
        accountTypeId: 'at-002',
        parentId: 'coa-006',
        isActive: true,
        activeFrom: '2023-01-01',
        fullGLPath: '2000.2100',
        level: 2,
        scopeType: 'global',
        children: [],
      },
    ],
  },
  {
    id: 'coa-008',
    segmentCode: '3000',
    segmentName: 'Equity',
    accountTypeId: 'at-003',
    isActive: true,
    activeFrom: '2023-01-01',
    fullGLPath: '3000',
    level: 1,
    scopeType: 'global',
    children: [],
  },
  {
    id: 'coa-009',
    segmentCode: '4000',
    segmentName: 'Revenue',
    accountTypeId: 'at-004',
    isActive: true,
    activeFrom: '2023-01-01',
    fullGLPath: '4000',
    level: 1,
    scopeType: 'global',
    children: [
      {
        id: 'coa-011',
        segmentCode: '4100',
        segmentName: 'Sales Revenue',
        accountTypeId: 'at-004',
        parentId: 'coa-009',
        isActive: true,
        activeFrom: '2023-01-01',
        fullGLPath: '4000.4100',
        level: 2,
        scopeType: 'global',
        children: [],
      },
      {
        id: 'coa-012',
        segmentCode: '4200',
        segmentName: 'Service Revenue',
        accountTypeId: 'at-004',
        parentId: 'coa-009',
        isActive: true,
        activeFrom: '2023-01-01',
        fullGLPath: '4000.4200',
        level: 2,
        scopeType: 'specific',
        applicableOrgUnitIds: ['org-002', 'org-003'],
        children: [],
      },
    ],
  },
  {
    id: 'coa-010',
    segmentCode: '5000',
    segmentName: 'Expenses',
    accountTypeId: 'at-005',
    isActive: true,
    activeFrom: '2023-01-01',
    fullGLPath: '5000',
    level: 1,
    scopeType: 'global',
    children: [
      {
        id: 'coa-013',
        segmentCode: '5100',
        segmentName: 'Operating Expenses',
        accountTypeId: 'at-005',
        parentId: 'coa-010',
        isActive: true,
        activeFrom: '2023-01-01',
        fullGLPath: '5000.5100',
        level: 2,
        scopeType: 'global',
        children: [
          {
            id: 'coa-014',
            segmentCode: '5110',
            segmentName: 'Salaries and Wages',
            accountTypeId: 'at-005',
            parentId: 'coa-013',
            isActive: true,
            activeFrom: '2023-01-01',
            fullGLPath: '5000.5100.5110',
            level: 3,
            scopeType: 'global',
            children: [
              {
                id: 'coa-021',
                segmentCode: '5111',
                segmentName: 'Base Salaries',
                accountTypeId: 'at-005',
                parentId: 'coa-014',
                isActive: true,
                activeFrom: '2023-01-01',
                fullGLPath: '5000.5100.5110.5111',
                level: 4,
                scopeType: 'global',
                children: [
                  {
                    id: 'coa-022',
                    segmentCode: '5111A',
                    segmentName: 'Executive Salaries',
                    accountTypeId: 'at-005',
                    parentId: 'coa-021',
                    isActive: true,
                    activeFrom: '2023-01-01',
                    fullGLPath: '5000.5100.5110.5111.5111A',
                    level: 5,
                    scopeType: 'specific',
                    applicableOrgUnitIds: ['org-001', 'org-002'],
                    children: [],
                  },
                  {
                    id: 'coa-023',
                    segmentCode: '5111B',
                    segmentName: 'Staff Salaries',
                    accountTypeId: 'at-005',
                    parentId: 'coa-021',
                    isActive: true,
                    activeFrom: '2023-01-01',
                    fullGLPath: '5000.5100.5110.5111.5111B',
                    level: 5,
                    scopeType: 'global',
                    children: [],
                  },
                ],
              },
            ],
          },
          {
            id: 'coa-015',
            segmentCode: '5120',
            segmentName: 'Office Supplies',
            accountTypeId: 'at-005',
            parentId: 'coa-013',
            isActive: true,
            activeFrom: '2023-01-01',
            fullGLPath: '5000.5100.5120',
            level: 3,
            scopeType: 'global',
            children: [],
          },
          {
            id: 'coa-016',
            segmentCode: '5130',
            segmentName: 'Rent and Utilities',
            accountTypeId: 'at-005',
            parentId: 'coa-013',
            isActive: true,
            activeFrom: '2023-01-01',
            fullGLPath: '5000.5100.5130',
            level: 3,
            scopeType: 'specific',
            applicableOrgUnitIds: ['org-002', 'org-005'],
            children: [],
          },
        ],
      },
      {
        id: 'coa-017',
        segmentCode: '5200',
        segmentName: 'Cost of Goods Sold',
        accountTypeId: 'at-005',
        parentId: 'coa-010',
        isActive: true,
        activeFrom: '2023-01-01',
        fullGLPath: '5000.5200',
        level: 2,
        scopeType: 'specific',
        applicableOrgUnitIds: ['org-001', 'org-004'],
        children: [],
      },
      {
        id: 'coa-018',
        segmentCode: '5300',
        segmentName: 'Marketing Expenses',
        accountTypeId: 'at-005',
        parentId: 'coa-010',
        isActive: true,
        activeFrom: '2023-01-01',
        fullGLPath: '5000.5300',
        level: 2,
        scopeType: 'specific',
        applicableOrgUnitIds: ['org-001', 'org-002', 'org-006'],
        children: [],
      },
    ],
  },
]

// Utility function to flatten OrgUnits
export function flattenOrgUnits(orgUnits: OrgUnit[]): OrgUnit[] {
  const result: OrgUnit[] = []
  const flatten = (units: OrgUnit[]) => {
    units.forEach((unit) => {
      result.push(unit)
      if (unit.childOrgUnits && unit.childOrgUnits.length > 0) {
        flatten(unit.childOrgUnits)
      }
    })
  }
  flatten(orgUnits)
  return result
}

// Utility function to flatten COA segments
export function flattenCOASegments(segments: COASegment[]): COASegment[] {
  const result: COASegment[] = []
  const flatten = (segs: COASegment[]) => {
    segs.forEach((seg) => {
      result.push(seg)
      if (seg.children && seg.children.length > 0) {
        flatten(seg.children)
      }
    })
  }
  flatten(segments)
  return result
}
